/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import {
    Button,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Pagination,
    SearchInput,
    Table,
    Dialog,
    DialogContent,
    Popover,
    PopoverTrigger,
    PopoverContent,
    DatePicker,
    Checkbox,
    FilterButton,
    Badge,
    MoreButton,
    AlertDialog,
    AlertDialogContent,
    ActionButton,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchRemitClaims } from "../../hooks/remits.queries";
import ListState from "../../../../components/ListState/ListState";
import {
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import {
    ClaimActivityType,
    ClaimStatus,
} from "../../../claims/types/claims.types";
import {
    IBatchRemit,
    IDeleteManualClaimPayload,
    IInsurancePayments,
    IRemit,
    RemitSource,
} from "../../types/remits.types";
import { PaymentStatus } from "../../../../utils/types/billing";
import { useDebounce } from "../../../../hooks/helpers";
import SearchRemitClaims from "../SearchRemitClaims/SearchRemitClaims";
import { IAppCustomDates } from "../../../../utils/types";
import {
    useDeleteManualRemitClaims,
    useRemoveElectronicRemitClaims,
} from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import {
    useUpdateClaimsActivity,
    useUpdateClaimStatus,
} from "../../../claims/hooks/claims.mutations";
import {
    getClaimActivityToDisplay,
    getClaimStatusFilterLabel,
    getClaimStatusTagStylesForDropdown,
} from "../../../claims/helpers/claims.helper";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import InfoIcon from "../../../../icons/Info";
import Loader from "../../../../shared-ui/Loader/Loader";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";

type Dialogs = "add-remit" | "delete-claim" | "bulk-delete-claims";

const getPaymentStatusForSingleRemitClaim = (
    remitClaims: IInsurancePayments[]
) => {
    const hasMatchedPayment = remitClaims.every(
        (claim) => claim.payment_status === "completed"
    );

    if (hasMatchedPayment) {
        return <Badge content="completed" type="success" />;
    }

    return <Badge content="pending" type="warning" />;
};

const getRemarkCodesForSingleRemitClaim = (
    insurancePayments: IInsurancePayments[]
) => {
    return Array.from(
        new Set(
            insurancePayments.flatMap((payment) => [
                ...(payment.remark_codes ?? []),
                ...Object.keys(payment.remark_codes_payment ?? {}),
            ])
        )
    );
};

const getRemitSourceTag = (remit: IRemit) => {
    if (remit.remit_source === RemitSource.MANUAL) {
        return <Badge content={remit.remit_source} type="neutral" />;
    }
    if (!remit.is_system_created) {
        return <Badge content="Manually added" type="success" />;
    }
    if (remit.manually_linked) {
        return <Badge content="Manually matched" type="info" />;
    }
    return <Badge content={remit.remit_source} type="info" />;
};

interface MatchedRemitClaimsProps {
    batchRemitInView: IBatchRemit | undefined;
}

export default function MatchedRemitClaims({
    batchRemitInView,
}: MatchedRemitClaimsProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [selectedClaimId, setSelectedClaimId] = React.useState<string | null>(
        null
    );
    const [selectedClaimIsManuallyAdded, setSelectedClaimIsManuallyAdded] =
        React.useState(false);
    const [selectedRemittanceId, setSelectedRemittanceId] = React.useState<
        string | null
    >(null);
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedClaimsData, setGetSelectedClaimsData] = React.useState<
        Row<IRemit>[]
    >([]);

    const navigate = useNavigate();
    const location = useLocation();

    // Get id from url params
    const params = useParams();
    const remitId = params.remitId || "";

    const noteId = searchParams.get("note_id");

    const search = searchParams.get("search_string") || "";

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const loggedInUser = useFetchUserDetails();

    const searchString = useDebounce(search, 500);
    const claimStatusFilter = searchParams.get("claim_status") || "";
    const paymentStatusFilter = searchParams.get("payment_status") || "";
    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const customDateFilter =
        (searchParams.get("date_range") as IAppCustomDates) || "";
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: searchParams.get("sort_attr") || "client_name",
        direction: searchParams.get("sort_order") || "ASC",
    });

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const selectedClaimsIds =
        getSelectedClaimsData &&
        getSelectedClaimsData.map(
            (item: Row<IRemit>) => item.original.claim_id
        );

    const sortableColumns = [
        "date_of_service",
        "cpt_code",
        "charged",
        "paid_amount",
        "outstanding",
        "claim_status",
        "expected_amount",
        "client_name",
        "payment_status",
        "remit_payment_date",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const { data, error, isLoading, isSuccess } = useFetchRemitClaims(
        {
            batch_remit_id: remitId,
            search_string: searchString,
            claim_status: claimStatusFilter,
            payment_status: paymentStatusFilter,
            page: pageFilter,
            limit: limitFilter,
            sort_attribute: handleConfigAttribute(),
            sort_order: sortConfig.direction,
            custom_date: customDateFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
        },
        Boolean(remitId)
    );

    // hook for updating claim status
    const patchClaimStatus = useUpdateClaimStatus();
    const updateClaimsActivity = useUpdateClaimsActivity();

    const claimsDetails =
        selectedClaimsIds &&
        selectedClaimsIds.map((id: string) =>
            data?.data.find((claim) => claim.claim_id === id)
        );

    const showUnassignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return isClaimAssignedToMe;
            }).length;

    const showAssignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return !isClaimAssignedToMe;
            }).length;

    const deleteManualRemitClaim = useDeleteManualRemitClaims(remitId);
    const removeElectronicRemitClaim = useRemoveElectronicRemitClaims(remitId);
    const isElectronicBatch =
        batchRemitInView?.remit_source === RemitSource.ELECTRONIC;

    const getRemoveClaimDescription = () => {
        if (!isElectronicBatch) {
            return "This action cannot be reversed and the claim won't be linked to this remit anymore";
        }
        if (selectedClaimIsManuallyAdded) {
            return "This deletes the line you manually added to this remit. The claim itself is not deleted.";
        }
        return "This unlinks the payment from the claim and removes it from this remit. The claim itself is not deleted.";
    };

    const handleUpdateClaimsActivity = (
        activityType: ClaimActivityType,
        claimId?: string
    ) => {
        const dataToSend = {
            claim_ids: claimId ? [claimId] : selectedClaimsIds,
            activity_type: activityType,
            notes: undefined,
        };
        updateClaimsActivity.mutate(dataToSend, {
            onSuccess: async (res) => {
                queryClient.invalidateQueries(["claims"]);
                queryClient.invalidateQueries(["claims-for-remit"]);
                await queryClient.invalidateQueries([
                    "remit-claims",
                    handleRemoveEmptyParamFromQueryParams({
                        batch_remit_id: remitId,
                    }),
                ]);

                toast({
                    mode: "success",
                    message:
                        res.message || "Claims activity updated successfully!",
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action at this time.",
                });
            },
        });
    };

    const statusOptionsForSelect = Object.values(ClaimStatus).map((status) => {
        return status;
    });

    const customDateForSelect = Object.values(IAppCustomDates).map((item) => {
        return item;
    });

    const paymentStatusOptionsForSelect = [
        PaymentStatus.COMPLETED,
        PaymentStatus.PENDING,
    ];

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleStatusChange = (value: string) => {
        if (value === "") searchParams.delete("claim_status");
        else searchParams.set("claim_status", value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handlePaymentStatusChange = (value: string) => {
        if (value === "") searchParams.delete("payment_status");
        else searchParams.set("payment_status", value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: string) => {
        if (value === "") searchParams.delete("date_range");
        else searchParams.set("date_range", value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleRowClick = (row: Row<IRemit>) => {
        if (row.original.client.client_id && row.original.note_id) {
            const urlToRedirectBackTo = `${
                location.pathname
            }?${searchParams.toString()}`;

            const objForRemitClaimsNaviagtion = {
                remitClaimsBackUrl: urlToRedirectBackTo,
                remitId,
                remitClaimsParams: searchParams.toString(),
            };

            localStorage.removeItem("remitClaimsBackUrl");
            localStorage.setItem(
                "remitClaimsMeta",
                JSON.stringify(objForRemitClaimsNaviagtion)
            );

            navigate(
                `/remits/${row.original.remittance_id}/${row.original.claim_id}/${row.original.client.client_id}/${row.original.note_id}?tab=remit&remit_in_view=${row.original.remittance_id}`
            );
        }
    };

    const prefillClaimStatusesForSearch = () => {
        searchParams.delete("invoice_statuses[]");
        searchParams.delete("claim_statuses[]");
        Object.values(ClaimStatus).forEach((status) => {
            if (status !== ClaimStatus.CLOSED) {
                searchParams.append("claim_statuses[]", status);
            }
        });
        setSearchParams(searchParams);
    };

    const handleOpenAddRemitDialog = () => {
        prefillClaimStatusesForSearch();
        setDialogInView("add-remit");
    };

    const handleCloseAddRemitDialog = () => {
        searchParams.delete("invoice_statuses[]");
        searchParams.delete("claim_statuses[]");
        setSearchParams(searchParams);
        setDialogInView(null);
    };

    const handleCloseBulkDeleteDialog = () => {
        setSelectedClaimId(null);
        setDialogInView(null);
        setRowSelection({});
    };

    const onClaimRemovedFromRemit = (message?: string) => {
        queryClient.invalidateQueries({
            queryKey: ["remit-claims"],
            refetchType: "all",
        });
        queryClient.invalidateQueries({
            queryKey: ["batch-remits"],
            refetchType: "all",
        });
        toast({
            mode: "success",
            message: message || "Claim removed successfully",
        });
        setSelectedClaimId(null);
        setSelectedRemittanceId(null);
        setDialogInView(null);
    };

    const handleDeleteRemitClaim = () => {
        if (!selectedClaimId) return;
        if (isElectronicBatch) {
            const payload: IDeleteManualClaimPayload = {
                claim_ids: [selectedClaimId],
            };
            if (selectedRemittanceId) {
                payload.remittance_ids = [selectedRemittanceId];
            }
            removeElectronicRemitClaim.mutate(payload, {
                onSuccess: (response) =>
                    onClaimRemovedFromRemit(response.message),
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not remove claim at this time",
                    });
                },
            });
        } else {
            deleteManualRemitClaim.mutate(
                { claim_ids: [selectedClaimId] },
                {
                    onSuccess: (response) =>
                        onClaimRemovedFromRemit(response.message),
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not remove claim at this time",
                        });
                    },
                }
            );
        }
    };

    const handleBulkDeleteRemitClaim = () => {
        if (selectedClaimsIds) {
            const payload = {
                claim_ids:
                    selectedClaimsIds && selectedClaimsIds.length > 0
                        ? selectedClaimsIds
                        : [],
            };

            deleteManualRemitClaim.mutate(payload, {
                onSuccess: (response) => {
                    queryClient.invalidateQueries([
                        "remit-claims",
                        handleRemoveEmptyParamFromQueryParams({
                            batch_remit_id: remitId,
                        }),
                    ]);
                    toast({
                        mode: "success",
                        message:
                            response.message || "Claim(s) deleted successfully",
                    });
                    handleCloseBulkDeleteDialog();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete claim(s) at this time",
                    });
                },
            });
        }
    };

    const handleChangeClaimStatus = (claimId: string, status: ClaimStatus) => {
        const changeStatusPayload = {
            claim_id: claimId,
            claim_status: status,
        };
        patchClaimStatus.mutate(changeStatusPayload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries([
                    "remit-claims",
                    handleRemoveEmptyParamFromQueryParams({
                        batch_remit_id: remitId,
                    }),
                ]);
                queryClient.invalidateQueries([
                    "get-claim",
                    { claim_id: claimId },
                ]);

                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);

                toast({
                    mode: "success",
                    message: `${response.message}`,
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update claim status at this time time",
                });
            },
        });
    };

    const columns: ColumnDef<IRemit>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) => (
                <span className="flex flex-col gap-y-1">
                    {formatDate(row.original.date_of_service)}
                    <span>{getRemitSourceTag(row.original)}</span>
                </span>
            ),
        },
        {
            accessorKey: "remit_payment_date",
            header: "CLAIM RECEIVED DATE",
            cell: ({ row }) =>
                row.original.remit_payment_date
                    ? formatDate(row.original.remit_payment_date, true)
                    : "--",
        },
        {
            accessorKey: "client_name",
            header: "CLIENT NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.client.first_name,
                    lastName: row.original.client.last_name,
                }),
        },
        {
            accessorKey: "claim_activities",
            header: "Assigned claim to",
            cell: ({ row }) => {
                const { usersNameToDisplay } = getClaimActivityToDisplay(
                    row.original.claim_activities,
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return usersNameToDisplay;
            },
        },
        {
            accessorKey: "cpt_code",
            header: "CPT CODE",
            cell: ({ row }) =>
                row.original.remittance_cpt_code || row.original.cpt_code,
        },
        {
            accessorKey: "charged",
            header: "CHARGED",
            cell: ({ row }) => showMoneyInAppFormat(row.original.charged),
        },
        {
            accessorKey: "paid_amount",
            header: "PAID",
            cell: ({ row }) => showMoneyInAppFormat(row.original.paid_amount),
        },
        {
            accessorKey: "insurance_writeoff",
            header: "WRITE OFF",
            cell: ({ row }) =>
                showMoneyInAppFormat(row.original.insurance_writeoff),
        },
        {
            accessorKey: "outstanding",
            header: "OUTSTANDING",
            cell: ({ row }) => (
                <span
                    className={cn("text-caption font-semibold", {
                        "text-danger": row.original.outstanding > 0,
                    })}
                >
                    {row.original.outstanding > 0
                        ? `(${showMoneyInAppFormat(row.original.outstanding)})`
                        : showMoneyInAppFormat(
                              Math.abs(row.original.outstanding)
                          )}
                </span>
            ),
        },
        {
            accessorKey: "insurance_expected",
            header: "EXPECTED",
            cell: ({ row }) =>
                !row.original.insurance_expected ? (
                    "--"
                ) : (
                    <span className="flex items-center gap-x-1">
                        {showMoneyInAppFormat(row.original.insurance_expected)}{" "}
                        {row.original.insurance_expected >
                            row.original.paid_amount && (
                            <ActionButton
                                variant="normal"
                                size="auto"
                                actionTitle="Expected amount is higher than paid amount"
                            >
                                <InfoIcon className="stroke-danger" />
                            </ActionButton>
                        )}
                    </span>
                ),
        },
        {
            accessorKey: "claim_status",
            header: "CLAIM STATUS",

            cell: ({ row }) =>
                row.original.claim_status ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton
                                className={cn(
                                    `capitalize !min-h-7 ![border:none] px-2 ${
                                        getClaimStatusTagStylesForDropdown(
                                            row.original.claim_status
                                        ).className
                                    }`
                                )}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {removeEnumUnderscore(
                                    row.original.claim_status
                                )}
                            </FilterButton>
                        </DropdownTrigger>

                        <DropdownContent width="auto" align="end">
                            {Object.values(ClaimStatus)
                                .filter(
                                    (status) =>
                                        status !== row.original.claim_status
                                )
                                .map((status) => (
                                    <DropdownItem
                                        key={status}
                                        onClick={(e) => {
                                            handleChangeClaimStatus(
                                                row.original.claim_id,
                                                status
                                            );
                                            e.stopPropagation();
                                        }}
                                    >
                                        {makeStringFirstLetterCapital(
                                            removeEnumUnderscore(status)
                                        )}
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                ) : (
                    "--"
                ),
        },
        {
            id: "payment_status",
            accessorKey: "insurance_payments",
            header: "Payment status",
            cell: ({ row }) =>
                getPaymentStatusForSingleRemitClaim(
                    row.original.insurance_payments
                ),
        },
        {
            id: "remark_codes",
            header: "Remark codes",
            cell: ({ row }) => {
                const remarkCodes = getRemarkCodesForSingleRemitClaim(
                    row.original.insurance_payments
                );

                return remarkCodes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {remarkCodes.map((code) => (
                            <Badge
                                key={code}
                                content={code.toUpperCase()}
                                type="success"
                            />
                        ))}
                    </div>
                ) : (
                    "--"
                );
            },
        },
        {
            accessorKey: "claim_id",
            header: "",
            cell: ({ row }) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    row.original.claim_activities,
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );
                return (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateClaimsActivity(
                                        isClaimAssignedToMe
                                            ? ClaimActivityType.UNLOCKED
                                            : ClaimActivityType.IN_PROGRESS,
                                        row.original.claim_id
                                    );
                                }}
                                disabled={updateClaimsActivity.isLoading}
                                className="normal-case"
                            >
                                {isClaimAssignedToMe
                                    ? "Unassign"
                                    : "Assign to me"}
                            </DropdownItem>
                            {batchRemitInView?.remit_source ===
                                RemitSource.MANUAL &&
                                !row.original.insurance_payments.find(
                                    (claim) =>
                                        claim.payment_status === "completed"
                                ) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedClaimId(
                                                row.original.claim_id
                                            );
                                            setDialogInView("delete-claim");
                                        }}
                                    >
                                        Delete
                                    </DropdownItem>
                                )}
                            {isElectronicBatch &&
                                (row.original.manually_linked ||
                                    !row.original.is_system_created) &&
                                !row.original.insurance_payments.find(
                                    (claim) =>
                                        claim.payment_status === "completed"
                                ) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedClaimId(
                                                row.original.claim_id
                                            );
                                            setSelectedRemittanceId(
                                                row.original.remittance_id
                                            );
                                            setSelectedClaimIsManuallyAdded(
                                                !row.original.is_system_created
                                            );
                                            setDialogInView("delete-claim");
                                        }}
                                    >
                                        Remove from remit
                                    </DropdownItem>
                                )}
                        </DropdownContent>
                    </Dropdown>
                );
            },
        },
    ];

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem(
            "remitclaimslist_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    return (
        <>
            <AlertDialog open={dialogInView === "bulk-delete-claims"}>
                <AlertDialogContent
                    title=" Are you sure you want to delete claims?"
                    description=" This action cannot be reversed and they won&lsquo;t be linked to this remit anymore"
                    onCancelClick={handleCloseBulkDeleteDialog}
                    onSaveClick={handleBulkDeleteRemitClaim}
                    isSaveBtnDisabled={deleteManualRemitClaim.isLoading}
                    isCancelBtnDisabled={deleteManualRemitClaim.isLoading}
                    isDeleting
                    saveText="Delete claims"
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "delete-claim"}>
                <AlertDialogContent
                    title={
                        isElectronicBatch
                            ? "Remove this claim from the remit?"
                            : "Are you sure you want to delete this claim?"
                    }
                    description={getRemoveClaimDescription()}
                    onCancelClick={() => {
                        setDialogInView(null);
                        setSelectedClaimId(null);
                        setSelectedRemittanceId(null);
                    }}
                    onSaveClick={handleDeleteRemitClaim}
                    isSaveBtnDisabled={
                        deleteManualRemitClaim.isLoading ||
                        removeElectronicRemitClaim.isLoading
                    }
                    isCancelBtnDisabled={
                        deleteManualRemitClaim.isLoading ||
                        removeElectronicRemitClaim.isLoading
                    }
                    isDeleting
                    saveText={
                        isElectronicBatch ? "Remove claim" : "Delete claim"
                    }
                />
            </AlertDialog>
            <Dialog open={dialogInView === "add-remit"}>
                <DialogContent
                    variant="center"
                    handleCloseDialog={handleCloseAddRemitDialog}
                    wrapperClass="w-[850px]"
                    classNames="px-0"
                    showFooter={false}
                >
                    <SearchRemitClaims
                        setShowAddRemitModal={(value: boolean) => {
                            if (value) {
                                setDialogInView("add-remit");
                            } else {
                                handleCloseAddRemitDialog();
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>
            <div className="flex items-center justify-between mb-[--spacing-header] flex-wrap gap-[--spacing-items]">
                <SearchInput
                    containerClass="w-[400px]"
                    placeholder="Search by client or claim no"
                    onChange={handleSetSearchString}
                    defaultValue={search}
                />

                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {claimStatusFilter
                                    ? truncateString(
                                          removeEnumUnderscore(
                                              claimStatusFilter
                                          )
                                      )
                                    : "Claim status"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent align="start" width="auto">
                            <DropdownItem
                                isSelected={!claimStatusFilter}
                                onClick={() => handleStatusChange("")}
                            >
                                All
                            </DropdownItem>
                            {statusOptionsForSelect.map((status) => (
                                <DropdownItem
                                    isSelected={claimStatusFilter === status}
                                    key={status}
                                    onClick={() => handleStatusChange(status)}
                                >
                                    {getClaimStatusFilterLabel(status)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {paymentStatusFilter
                                    ? truncateString(
                                          removeEnumUnderscore(
                                              paymentStatusFilter
                                          )
                                      )
                                    : "Payment status"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent align="end" width={200}>
                            <DropdownItem
                                isSelected={!paymentStatusFilter}
                                onClick={() => handlePaymentStatusChange("")}
                            >
                                All
                            </DropdownItem>
                            {paymentStatusOptionsForSelect.map((status) => (
                                <DropdownItem
                                    isSelected={paymentStatusFilter === status}
                                    key={status}
                                    onClick={() =>
                                        handlePaymentStatusChange(status)
                                    }
                                >
                                    {removeEnumUnderscore(status)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {customDateFilter
                                    ? truncateString(
                                          removeEnumUnderscore(customDateFilter)
                                      )
                                    : "Custom Date"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent align="start" width={200}>
                            {customDateForSelect.map((range) => (
                                <DropdownItem
                                    isSelected={customDateFilter === range}
                                    key={range}
                                    onClick={() =>
                                        handleCustomDateChange(range)
                                    }
                                >
                                    {removeEnumUnderscore(range)}{" "}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>

                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton>Date</FilterButton>
                        </PopoverTrigger>
                        <PopoverContent align="end">
                            <div className="mb-1">
                                <DatePicker
                                    label="From"
                                    placeholderText="MM/DD/YYYY"
                                    selected={fromDateFilterToDate}
                                    onChange={handleFromDateFilterChange}
                                    maxDate={
                                        toDateFilterToDate ||
                                        new Date(Date.now())
                                    }
                                    isClearable
                                />
                            </div>

                            <DatePicker
                                label="To"
                                placeholderText="MM/DD/YYYY"
                                selected={toDateFilterToDate}
                                onChange={handleToDateFilterChange}
                                className="hover:border-primary"
                                minDate={fromDateFilterToDate}
                                isClearable
                            />
                        </PopoverContent>
                    </Popover>

                    {batchRemitInView && (
                        <Button onClick={handleOpenAddRemitDialog}>
                            Search &amp; add claims
                        </Button>
                    )}
                </div>
            </div>

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display claims at this time please try again later"
                    }
                />
            )}
            {isLoading && <Loader />}

            {data && isSuccess && (
                <>
                    {data.data.length === 0 && (
                        <div className="flex items-center justify-center flex-col h-[150px] gap-2 max-w-[180px] mx-auto">
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-4 shrink-0 stroke-textSec"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <p className="text-sm font-medium text-center text-textSec">
                                Search for a client to add a claims manually
                            </p>
                        </div>
                    )}

                    {data.data.length > 0 && (
                        <>
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount > 0 && (
                                <div className="flex items-center mb-4 gap-x-[--spacing-items]">
                                    <div className="font-medium text-base">
                                        {
                                            getTotalAmountOfTableRowsSelected(
                                                rowSelection
                                            ).amountString
                                        }
                                        :
                                    </div>
                                    <div className="flex items-center flex-wrap gap-x-[--spacing-items]">
                                        {showAssignClaimsButton ? (
                                            <Button
                                                variant="secondary"
                                                onClick={() =>
                                                    handleUpdateClaimsActivity(
                                                        ClaimActivityType.IN_PROGRESS
                                                    )
                                                }
                                                disabled={
                                                    updateClaimsActivity.isLoading
                                                }
                                            >
                                                Assign claims to me
                                            </Button>
                                        ) : null}
                                        {showUnassignClaimsButton ? (
                                            <Button
                                                variant="secondary"
                                                onClick={() =>
                                                    handleUpdateClaimsActivity(
                                                        ClaimActivityType.UNLOCKED
                                                    )
                                                }
                                                disabled={
                                                    updateClaimsActivity.isLoading
                                                }
                                            >
                                                Unassign claims
                                            </Button>
                                        ) : null}
                                        {!isElectronicBatch && (
                                            <Button
                                                variant="danger"
                                                onClick={() =>
                                                    setDialogInView(
                                                        "bulk-delete-claims"
                                                    )
                                                }
                                            >
                                                Delete claims
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <Table
                                columns={columns}
                                data={data.data}
                                hasPagination={data.total_count > 1}
                                handleRowClick={handleRowClick}
                                sortableColumns={sortableColumns}
                                sortConfig={sortConfig}
                                setSortConfig={setSortConfig}
                                setRowSelection={setRowSelection}
                                rowSelection={rowSelection}
                                setGetSelectedData={setGetSelectedClaimsData}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
